import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import jsonp from 'jsonp';
import 'assets/styles/Footer.css';
import SocialLink from 'components/SocialLink';
import video from 'assets/videos/footer.mp4';
import { ReactComponent as Spinner } from 'assets/images/spinner.svg';

export default function Footer() {
  const { navigation, social, mailchimp } = require('configs/settings.json');
  const [status, setStatus] = useState('idle');
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors } } = useForm({ mode: 'onChange' });
  const currentYear = new Date().getFullYear();

  const onSubmit = data => {
    setStatus('loading');

    jsonp(`${mailchimp.url}&EMAIL=${data.email}`, { param: 'c' }, (_, data) => {
      const { result } = data;

      if (result === 'success') {
        setStatus('success');
      } else {
        setStatus('idle');
      }
    });
  }

  return (
    <footer>
      <div className="container">
        <div className='footer-logo'>
          <video autoPlay playsInline muted loop aria-hidden='true'>
            <source src={video} type="video/mp4" />
            <track src="video/video.srt" kind="subtitles" srcLang="en" label="English" />
            Your browser does not support the video tag.
          </video>
          <span className='sr-only'>KOLLECTIFF</span>
        </div>

        <div className="footer-bottom">

          <div className="footer-left">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className='tagline'>
              A Creative Technology Company
            </motion.p>

            <ul className='footer-links'>
              {navigation.footer.map((item) =>
                <li key={item.link}>
                  <Link to={item.link} className='link-sm link-animated'>{item.text}</Link>
                </li>
              )}
            </ul>

            <ul className='footer-social'>
              {social.map(props =>
                <SocialLink key={props.title} {...props} />
              )}
            </ul>
          </div>

          <div className="footer-right">
            <form onSubmit={handleSubmit(onSubmit)}>
              <label htmlFor="newsletter" className='k-text-md'>Stay updated</label>
              <div className="relative">
                <input
                  type="email"
                  id="newsletter"
                  disabled={status === 'loading' || status === 'success'}
                  placeholder="Sign up for our newsletter"
                  {...register('email', {
                    required: true,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                    }
                  })} />
                <button
                  type="submit"
                  className={classNames({ '!text-kollectiff-lime': !errors.email && watch('email') })}
                  disabled={status === 'loading' || status === 'success'}>
                  <AnimatePresence mode="wait">
                    {status === 'idle' && (
                      <motion.span
                        key="submit"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        submit
                      </motion.span>
                    )}
                    {status === 'loading' && (
                      <motion.span
                        key="spinner"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Spinner className="fill-kollectiff-lime" />
                      </motion.span>
                    )}
                    {status === 'success' && (
                      <motion.span
                        className="text-kollectiff-lime"
                        key="suscribed"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        suscribed
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </div>
            </form>
            
            <p className="k-text-md">KOLLECTIFF™ {currentYear} | All right reserved ©</p>
            
          </div>
        </div>
      </div>
    </footer>
  );
}
